import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  CopyIcon, DownloadSimpleIcon, TerminalWindowIcon, StackIcon,
  BooksIcon, WarningIcon, ArrowSquareOutIcon, XIcon, SealCheckIcon, ShieldCheckIcon, BroomIcon,
} from "@phosphor-icons/react";
import { tokenizeLine } from "@/lib/highlight";

const cleanCli = (t) =>
  t.split("\n").filter((l) => !l.trim().startsWith("#")).join("\n").replace(/\n{3,}/g, "\n\n").trim() + "\n";

function annotate(text) {
  const rows = [];
  let inU = false;
  for (const line of text.split("\n")) {
    const s = line.trim();
    if (s.startsWith("#!UNVERIFIED:")) { rows.push({ line: s.slice("#!UNVERIFIED:".length).trim(), type: "u-start" }); inU = true; }
    else if (s.startsWith("#!END-UNVERIFIED")) { rows.push({ line: "", type: "u-end" }); inU = false; }
    else rows.push({ line, type: inU ? "u-body" : "normal" });
  }
  return rows;
}

const slug = (s) => (s || "").replace(/[^a-zA-Z0-9]+/g, "").slice(0, 24) || "engineer";
const today = () => new Date().toISOString().slice(0, 10).replace(/-/g, "");

export const ConfigEditor = ({ config, meta, loading, streaming, citations = [], unverified = [] }) => {
  const scrollRef = useRef(null);
  const [showSources, setShowSources] = useState(false);
  const [signStep, setSignStep] = useState(0); // 0 closed, 1 reminder-1, 2 reminder-2
  const [signing, setSigning] = useState(false);
  const [signedOff, setSignedOff] = useState(false);
  const [cleanView, setCleanView] = useState(false);
  const [projCode, setProjCode] = useState("");
  const [version, setVersion] = useState("0.1");

  const engineerName = meta?.engineer_name || meta?.signed_off_by || "";
  const projectName = meta?.project_name || "";
  const genId = meta?.gen_id || meta?.id;

  useEffect(() => { setSignedOff(!!meta?.signed_off); }, [meta]);
  useEffect(() => { setProjCode(meta?.project_code || ""); setVersion(meta?.version || "0.1"); }, [meta]);
  useEffect(() => { if (scrollRef.current && streaming) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [config, streaming]);

  const displayText = config ? (cleanView ? cleanCli(config) : config) : "";

  const copy = async () => { await navigator.clipboard.writeText(displayText); toast.success(cleanView ? "Clean CLI copied" : "Configuration copied"); };

  const doDownload = async () => {
    const vlabel = (meta?.vendors && meta.vendors.length ? meta.vendors : [meta?.vendor]).filter(Boolean).join("-") || "config";
    const verPart = version ? `-v${version}` : "";
    const nameBase = slug(projectName || engineerName || vlabel);
    const fname = projCode ? `${projCode}${verPart}` : `netforge-${nameBase}${verPart}`;
    if (genId) {
      try {
        const res = await api.get(`/history/${genId}/bundle`, { responseType: "blob" });
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a");
        a.href = url; a.download = `${fname}.zip`; a.click();
        URL.revokeObjectURL(url);
        toast.success("Downloaded per-device config bundle (.zip)");
        return;
      } catch { /* fall through to single clean file */ }
    }
    const blob = new Blob([cleanCli(config)], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${fname}.conf`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Downloaded config file");
  };

  const onDownloadClick = () => {
    if (signedOff) return doDownload();
    setSignStep(1);
  };

  const confirmSignoff = async () => {
    setSigning(true);
    try {
      if (genId) {
        const { data } = await api.post(`/history/${genId}/signoff`);
        if (data?.project_code) setProjCode(data.project_code);
        if (data?.version) setVersion(data.version);
      }
      setSignedOff(true);
      setSignStep(0);
      toast.success(`Signed off${engineerName ? ` by ${engineerName}` : ""} — downloading`);
      await doDownload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sign-off failed");
    }
    setSigning(false);
  };

  const rows = displayText ? annotate(displayText) : [];
  const vendorLabel = (meta?.vendors && meta.vendors.length ? meta.vendors : [meta?.vendor]).filter(Boolean).join(" + ");

  return (
    <div className="flex flex-col h-full bg-[#050505]">
      {/* toolbar */}
      <div className="h-11 shrink-0 border-b border-zinc-800 flex items-center justify-between px-4 bg-zinc-950/60">
        <div className="flex items-center gap-2 min-w-0">
          <TerminalWindowIcon className="h-4 w-4 text-zinc-500 shrink-0" />
          <span className="font-mono text-xs text-zinc-400 truncate">{config ? `netforge-${vendorLabel || "config"}.conf` : "no output"}</span>
          {meta && config && (
            <>
              {projectName && (
                <span data-testid="project-name-badge" className="inline-flex items-center gap-1 rounded-sm border border-zinc-700 bg-zinc-900 px-2 py-0.5 text-[10px] text-zinc-300 max-w-[160px] truncate">
                  {projectName}
                </span>
              )}
              {projCode && (
                <span data-testid="project-code-badge" className="inline-flex items-center gap-1 rounded-sm border border-cyan-500/40 bg-cyan-500/10 px-2 py-0.5 font-mono text-[10px] text-cyan-300">
                  {projCode}
                </span>
              )}
              <span data-testid="version-badge" className="inline-flex items-center gap-1 rounded-sm border border-zinc-800 bg-zinc-900 px-2 py-0.5 font-mono text-[10px] text-zinc-400">
                v{version}
              </span>
              <span className="ml-1 inline-flex items-center gap-1 rounded-sm border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-300">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> AI · Claude
              </span>
              {meta.is_revision && (
                <span className="inline-flex items-center gap-1 rounded-sm border border-cyan-500/30 bg-cyan-500/10 px-2 py-0.5 text-[10px] text-cyan-300">revised</span>
              )}
              {meta.devices_count != null && (
                <span className="inline-flex items-center gap-1 rounded-sm border border-zinc-800 bg-zinc-900 px-2 py-0.5 text-[10px] text-zinc-400">
                  <StackIcon className="h-3 w-3" /> {meta.devices_count} devices
                </span>
              )}
              {signedOff && (
                <span data-testid="signed-off-badge" className="inline-flex items-center gap-1 rounded-sm border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] text-emerald-300">
                  <SealCheckIcon weight="fill" className="h-3 w-3" /> signed off
                </span>
              )}
            </>
          )}
        </div>
        {config && (
          <div className="flex items-center gap-2">
            {unverified.length > 0 && (
              <span data-testid="unverified-count" className="inline-flex items-center gap-1 rounded-sm border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-[11px] font-medium text-amber-300">
                <WarningIcon weight="fill" className="h-3.5 w-3.5" /> {unverified.length} unverified
              </span>
            )}
            <button data-testid="clean-toggle-btn" onClick={() => setCleanView((v) => !v)}
              className={`flex items-center gap-1.5 rounded-sm border px-2.5 py-1.5 text-xs transition-colors ${cleanView ? "border-cyan-500/50 bg-cyan-500/10 text-cyan-300" : "border-zinc-800 bg-zinc-900 text-zinc-300 hover:bg-zinc-800"}`}>
              <BroomIcon className="h-3.5 w-3.5" /> {cleanView ? "Annotated" : "Clean CLI"}
            </button>
            <button data-testid="sources-btn" onClick={() => setShowSources((s) => !s)}
              className={`flex items-center gap-1.5 rounded-sm border px-2.5 py-1.5 text-xs transition-colors ${showSources ? "border-cyan-500/50 bg-cyan-500/10 text-cyan-300" : "border-zinc-800 bg-zinc-900 text-zinc-300 hover:bg-zinc-800"}`}>
              <BooksIcon className="h-3.5 w-3.5" /> Sources {citations.length ? `(${citations.length})` : ""}
            </button>
            <button data-testid="copy-config-btn" onClick={copy}
              className="flex items-center gap-1.5 rounded-sm border border-zinc-800 bg-zinc-900 hover:bg-zinc-800 px-2.5 py-1.5 text-xs text-zinc-300 transition-colors">
              <CopyIcon className="h-3.5 w-3.5" /> Copy
            </button>
            <button data-testid="download-config-btn" onClick={onDownloadClick}
              className="flex items-center gap-1.5 rounded-sm bg-cyan-500 hover:bg-cyan-400 px-2.5 py-1.5 text-xs font-medium text-zinc-950 transition-colors">
              <DownloadSimpleIcon weight="bold" className="h-3.5 w-3.5" /> {signedOff ? "Download" : "Sign off & Download"}
            </button>
          </div>
        )}
      </div>

      {/* body */}
      <div ref={scrollRef} className="relative flex-1 overflow-auto">
        {loading && !config && (
          <div className="scanline absolute inset-0 z-10 flex items-center justify-center bg-[#050505]/60">
            <div className="font-mono text-sm text-zinc-500">Claude is designing the configuration<span className="caret">▋</span></div>
          </div>
        )}

        {!config && !loading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center px-8 text-center">
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                backgroundImage:
                  "linear-gradient(to right, #14161b 1px, transparent 1px), linear-gradient(to bottom, #14161b 1px, transparent 1px)",
                backgroundSize: "46px 46px",
                maskImage: "radial-gradient(ellipse 58% 55% at 50% 42%, #000 25%, transparent 72%)",
                WebkitMaskImage: "radial-gradient(ellipse 58% 55% at 50% 42%, #000 25%, transparent 72%)",
              }}
            />
            <div className="relative z-10 w-full max-w-xs">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl border border-cyan-500/20 bg-cyan-500/[0.06] shadow-[0_0_40px_-8px_rgba(6,182,212,0.35)]">
                <TerminalWindowIcon className="h-7 w-7 text-cyan-400/80" />
              </div>
              <p className="mt-5 font-head text-lg text-zinc-200">Awaiting configuration</p>
              <p className="mt-2 text-sm text-zinc-500">Your grounded device configuration renders here once you build.</p>
              <div className="mt-6 space-y-1.5 text-left">
                {[["1", "Pick vendor(s) & firmware"], ["2", "Load the approved Scope of Work"], ["3", "Answer a few design questions"], ["4", "Build a fully-grounded config"]].map(([n, t]) => (
                  <div key={n} className="flex items-center gap-3 rounded-md border border-zinc-800/60 bg-zinc-900/30 px-3 py-2">
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-zinc-800 text-[10px] font-bold text-cyan-400">{n}</span>
                    <span className="text-xs text-zinc-400">{t}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {config && (
          <div className="flex min-h-full font-mono text-[13px] leading-6 fade-up">
            <div className="select-none border-r border-zinc-800 bg-[#0A0A0C] px-3 py-4 text-right text-zinc-700">
              {rows.map((_, i) => <div key={i}>{i + 1}</div>)}
            </div>
            <pre className="flex-1 overflow-visible px-4 py-4 whitespace-pre-wrap break-words">
              {rows.map((r, i) => {
                if (r.type === "u-start")
                  return (
                    <div key={i} className="my-0.5 flex items-center gap-1.5 rounded-sm border-l-2 border-amber-500 bg-amber-500/10 px-2 py-0.5 text-amber-300">
                      <WarningIcon weight="fill" className="h-3.5 w-3.5 shrink-0" />
                      <span className="text-[12px]">NOT DOC-VERIFIED — {r.line}</span>
                    </div>
                  );
                if (r.type === "u-end") return <div key={i} className="mb-1 border-b border-dashed border-amber-500/30" />;
                if (r.type === "u-body")
                  return (
                    <div key={i} className="border-l-2 border-amber-500/40 bg-amber-500/[0.05] pl-2">
                      {tokenizeLine(r.line).map((tok, j) => <span key={j} className={tok.cls}>{tok.text}</span>)}
                    </div>
                  );
                return (
                  <div key={i}>
                    {tokenizeLine(r.line).map((tok, j) => <span key={j} className={tok.cls}>{tok.text}</span>)}
                    {i === rows.length - 1 && streaming && <span className="caret text-cyan-400">▋</span>}
                  </div>
                );
              })}
            </pre>
          </div>
        )}

        {/* sources panel */}
        {config && showSources && (
          <div data-testid="sources-panel" className="absolute bottom-0 left-0 right-0 z-20 max-h-[55%] overflow-y-auto border-t border-zinc-800 bg-zinc-950/95 backdrop-blur-md">
            <div className="sticky top-0 flex items-center justify-between border-b border-zinc-800 bg-zinc-950 px-4 py-2">
              <span className="text-xs font-semibold uppercase tracking-widest text-zinc-400">Provenance</span>
              <button data-testid="close-sources-btn" onClick={() => setShowSources(false)} className="text-zinc-500 hover:text-zinc-200"><XIcon className="h-4 w-4" /></button>
            </div>
            <div className="p-4 space-y-4">
              {unverified.length > 0 && (
                <div>
                  <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-amber-300"><WarningIcon weight="fill" className="h-3.5 w-3.5" /> Not doc-verified — review before deploying</p>
                  <ul className="space-y-1.5">
                    {unverified.map((u, i) => (
                      <li key={i} data-testid={`unverified-item-${i}`} className="rounded-sm border border-amber-500/25 bg-amber-500/[0.05] px-3 py-2 text-xs text-zinc-300">
                        {u.reason}
                        {u.review && <a href={u.review} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center gap-1 text-amber-400 hover:underline">review <ArrowSquareOutIcon className="h-3 w-3" /></a>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div>
                <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-cyan-300"><BooksIcon className="h-3.5 w-3.5" /> Grounded in official vendor documentation</p>
                {citations.length === 0 ? <p className="text-xs text-zinc-600">No citations recorded for this generation.</p> : (
                  <ul className="space-y-1.5">
                    {citations.map((c, i) => (
                      <li key={i} data-testid={`citation-${i}`} className="rounded-sm border border-zinc-800 bg-zinc-900/50 px-3 py-2">
                        <a href={c.url} target="_blank" rel="noreferrer" className="flex items-start gap-1.5 text-xs text-zinc-200 hover:text-cyan-300">
                          <ArrowSquareOutIcon className="mt-0.5 h-3 w-3 shrink-0 text-zinc-500" />
                          <span><span className="font-medium">{c.title}</span><span className="block text-[10px] text-zinc-500">{c.source}{c.kind === "community" ? " · community (review)" : ""}</span></span>
                        </a>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}

        {/* sign-off gate (2 reminders) */}
        {signStep > 0 && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6" data-testid="signoff-modal">
            <div className="w-full max-w-md rounded-lg border border-zinc-800 bg-zinc-950 p-6">
              <div className="mb-3 flex items-center gap-2 text-cyan-400">
                <ShieldCheckIcon weight="fill" className="h-5 w-5" />
                <h3 className="font-head text-lg text-zinc-100">Engineer sign-off {signStep}/2</h3>
              </div>
              {signStep === 1 ? (
                <p data-testid="signoff-reminder-1" className="text-sm text-zinc-400">
                  Are you sure this configuration is <span className="text-zinc-100">correct and complete</span> for the customer's requirement?
                </p>
              ) : (
                <p data-testid="signoff-reminder-2" className="text-sm text-zinc-400">
                  Have you <span className="text-zinc-100">reviewed every section</span>, including
                  {unverified.length > 0
                    ? <span className="text-amber-300"> the {unverified.length} block(s) flagged “not doc-verified”</span>
                    : " anything flagged for review"}? Signing off records your name against this config.
                </p>
              )}
              {engineerName && <p className="mt-3 text-xs text-zinc-600">Signing off as <span className="text-zinc-300">{engineerName}</span></p>}
              <div className="mt-6 flex justify-end gap-2">
                <button data-testid="signoff-cancel-btn" onClick={() => setSignStep(0)} disabled={signing}
                  className="rounded-sm border border-zinc-700 px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-800 disabled:opacity-60">Cancel</button>
                {signStep === 1 ? (
                  <button data-testid="signoff-continue-btn" onClick={() => setSignStep(2)}
                    className="rounded-sm bg-cyan-500 px-4 py-2 text-sm font-semibold text-zinc-950 hover:bg-cyan-400">Yes, continue</button>
                ) : (
                  <button data-testid="signoff-confirm-btn" onClick={confirmSignoff} disabled={signing}
                    className="flex items-center gap-1.5 rounded-sm bg-emerald-500 px-4 py-2 text-sm font-semibold text-zinc-950 hover:bg-emerald-400 disabled:opacity-60">
                    <SealCheckIcon weight="fill" className="h-4 w-4" /> {signing ? "Signing…" : "Sign off & download"}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
